// Learning Value: is it worth learning more before deciding to build? The net value of
// shipping is a normal belief from a 90% range (it may be negative; not shipping is 0).
// Each way of learning reports the value with noise and bias; its expected value of
// sample information (src/lib/voi.js) is set against its cost in team-days.
// Source: docs/backlog-prioritization-research.md#3-value-of-information. Tests: model.test.js.

import { evpi, evsi } from "../lib/voi.js";
import { normalFrom90 } from "../lib/ranges.js";
import { normalCdf } from "../lib/stats.js";

/**
 * Problems with the decision's value range, in words; empty when usable.
 * @param {{ low: number, high: number }} value
 * @returns {string[]}
 */
export function valueIssues(value) {
  if (!(Number.isFinite(value.low) && Number.isFinite(value.high))) return ["The value range needs both ends."];
  if (!(value.high > value.low)) return ["The value range needs a high above its low: with no uncertainty there is nothing to learn."];
  return [];
}

/**
 * Value of each way to learn, for one decision.
 * @param {{ value: {low:number, high:number}, dayValue: number, methods: {label:string, noise:number, bias:number, days:number}[] }} input
 * @returns {object}
 */
export function analyseLearning({ value, dayValue, methods }) {
  const issues = valueIssues(value);
  if (issues.length) return { issues };
  const belief = normalFrom90(value.low, value.high);
  const perfect = evpi(belief);
  const rows = methods.map((m, index) => {
    const evidence = { noiseSd: m.noise * belief.sd, biasSd: m.bias * belief.sd };
    const gain = evsi(belief, evidence);
    const cost = Math.max(0, m.days) * dayValue;
    return {
      index,
      label: m.label,
      evsi: gain,
      cost,
      net: gain - cost,
      ceiling: evsi(belief, { noiseSd: 0, biasSd: evidence.biasSd }),
      share: perfect > 0 ? gain / perfect : 0,
    };
  });
  const best = rows.reduce((b, r) => (b == null || r.net > b.net ? r : b), null);
  const learn = best != null && best.net > 0;
  return {
    issues,
    belief,
    chanceWorthIt: normalCdf(belief.mean / belief.sd),
    decideNow: belief.mean > 0 ? "build" : "drop",
    evpi: perfect,
    rows,
    best,
    learn,
    // Perfect information is worth under 1% of the stake: no study could change the call.
    settled: perfect < 0.01 * Math.max(Math.abs(belief.mean), belief.sd),
  };
}

export function fmt(x) {
  if (!Number.isFinite(x)) return "—";
  const a = Math.abs(x);
  return a >= 100 ? Math.round(x).toLocaleString("en-US") : a >= 10 ? x.toFixed(1) : x.toFixed(2);
}
export const pct = (p) => (Number.isFinite(p) ? `${Math.round(p * 100)}%` : "—");

/**
 * Plain sentences for a ticket, a channel or an agent's prompt.
 * @param {object} r analyseLearning result
 * @param {{ decision: string, unit: string }} s
 * @returns {string[]}
 */
export function summaryLines(r, { decision, unit }) {
  if (r.issues.length) return [];
  const lines = [
    `${decision}: ${pct(r.chanceWorthIt)} chance building it is worth more than not; deciding now would ${r.decideNow === "build" ? "build it" : "drop it"}.`,
    `Perfect knowledge of its value is worth at most ${fmt(r.evpi)} ${unit}; no study can be worth more than that.`,
  ];
  if (r.settled) lines.push("No study could change this decision: decide now.");
  else if (r.learn) lines.push(`Best way to learn first: ${r.best.label}, expected to add ${fmt(r.best.evsi)} ${unit} for a cost of ${fmt(r.best.cost)} (net ${fmt(r.best.net)}).`);
  else lines.push("None of the listed ways to learn is worth its cost: decide now.");
  lines.push("Noise and bias per method are starting assumptions, stated as multiples of today's uncertainty, not measurements.");
  return lines;
}
